import { ExternalLink, MapPin, MessageSquare } from "lucide-react";
import { CompanyAvatar } from "./company-avatar";
import { UpvoteButton } from "./upvote-button";
import { MetaRow, RBTag, StatusChip } from "./rb-ui";
import {
  getRowDetails,
  getSource,
  resolveRaise,
  type Application,
} from "./deal-fields";

/*
 * The deal card shared by the global Raiseboard and every lookbook.
 *
 * A lookbook is meant to read exactly like the global board, so the card lives
 * here rather than being copied — the two pages differ in what they fetch and
 * where a card navigates, not in how a deal looks.
 */

export function HeroStat({ value, label }: { value: number; label: string }) {
  return (
    <div className="text-center">
      <p className="font-serif text-3xl leading-none">
        {value >= 1000 ? `${(value / 1000).toFixed(1).replace(/\.0$/, "")}k` : value}
      </p>
      <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-background/50">
        {label}
      </p>
    </div>
  );
}

export function BoardRow({
  application,
  rank,
  vote,
  onVote,
  onOpen,
  showSource = true,
}: {
  application: Application;
  rank: number;
  vote: { voted: boolean; count: number };
  onVote: () => void;
  onOpen: () => void;
  /**
   * "From <board>" only earns its place where deals come from many workspaces.
   * A lookbook is one workspace's own selection, so it names the same board on
   * every row and is hidden there.
   */
  showSource?: boolean;
}) {
  const { company, stageLabel, industries, bio, country } =
    getRowDetails(application);
  const source = getSource(application);
  const raise = resolveRaise(application);

  // Every industry the deal carries, so what the row shows is exactly what the
  // Industry filter can match. Rows stay one height by capping the visible
  // chips and rolling the rest into a +N.
  const allTags = [
    ...industries,
    ...(stageLabel && stageLabel !== "Unknown" ? [stageLabel] : []),
  ].filter((t, i, arr) => arr.indexOf(t) === i);
  const VISIBLE_TAGS = 3;
  const tags = allTags.slice(0, VISIBLE_TAGS);
  const overflow = allTags.length - tags.length;

  return (
    /* The stretched overlay button is the single "open deal profile" control,
       so the card stays a non-interactive container and the upvote / board
       link remain independently focusable (no nested interactive elements). */
    <div className="group relative rounded-2xl border border-border bg-card p-4 shadow-sm transition-colors hover:border-foreground/20 sm:p-5">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`View ${company?.company_name ?? "company"} details`}
        className="absolute inset-0 z-0 cursor-pointer rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
      />

      <div className="pointer-events-none relative z-10 flex items-start gap-3 sm:gap-4">
        <span className="hidden w-7 shrink-0 pt-3 text-right text-sm font-medium text-muted-foreground/70 sm:block">
          #{rank}
        </span>

        <CompanyAvatar
          size="xl"
          tone="soft"
          logoUrl={company?.logo_url}
          name={company?.company_name}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="font-serif text-xl leading-tight">
              {company?.company_name ?? "Untitled"}
            </h3>
            <StatusChip />
          </div>

          {bio && (
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
              {bio}
            </p>
          )}

          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {raise && (
              <span className="inline-flex items-center rounded-full bg-foreground/[0.06] px-2.5 py-0.5 text-[11px] font-semibold">
                {raise}
              </span>
            )}
            {tags.map((t) => (
              <RBTag key={t}>{t}</RBTag>
            ))}
            {overflow > 0 && (
              <span
                className="text-[11px] font-medium text-muted-foreground"
                title={allTags.slice(VISIBLE_TAGS).join(", ")}
              >
                +{overflow}
              </span>
            )}
          </div>

          <MetaRow
            className="mt-2.5 text-xs"
            items={[
              country && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3 shrink-0" />
                  {country}
                </span>
              ),
              showSource &&
                source.name &&
                (source.slug && source.enabled ? (
                  <a
                    href={`/rb/${source.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="pointer-events-auto relative z-10 inline-flex items-center gap-0.5 hover:text-foreground"
                  >
                    From {source.name}
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                ) : (
                  `From ${source.name}`
                )),
              !!application.comment_count && (
                <span className="inline-flex items-center gap-1">
                  <MessageSquare className="h-3 w-3 shrink-0" />
                  {application.comment_count}
                </span>
              ),
            ]}
          />
        </div>

        <div className="pointer-events-auto relative z-10 pt-0.5">
          <UpvoteButton
            variant="outline"
            count={vote.count}
            voted={vote.voted}
            onClick={(e) => {
              e.stopPropagation();
              onVote();
            }}
          />
        </div>
      </div>
    </div>
  );
}
